import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import { imageurl } from "../../../App";
import { getStoreFreeShippingThreshold } from "../../../util/guestCartService";
import { getProductImageUrl, getDefaultStoreLogo, handleProductImageError, isDefaultStoreImage } from "../../../util/storeLogoHelper";
import {
  FaHeart,
  FaShoppingBag,
  FaShieldAlt,
  FaTruck,
  FaUndo,
  FaStar,
  FaRegStar,
  FaCheckCircle,
  FaUserCheck,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaTimes,
  FaCamera,
  FaFire
} from "react-icons/fa";
import SEOHead from "../../common/SEOHead";
import useProduct from "../../../hooks/useProduct";
import { ActiveThemeContext } from "../../../themes/themeCache";
import { productPageSettings, productPageSections, PRODUCT_GALLERY_COLUMNS } from "../../../themes/productPageSettings";
import "../../../assets/frontend/styles/product-layout.css";
import SectionRenderer from "../../../themes/SectionRenderer";
import FrequentlyBought from "./FrequentlyBought";
import { PageColors, pageWords, PRODUCT_COLORS, PRODUCT_SCOPE, PRODUCT_TEXTS } from "../../../themes/pageCustomization";
import { SECTION_DEFINITIONS } from "../../../themes/themeRegistry";

export default function ProductDetails(props) {
  const activeTheme = useContext(ActiveThemeContext);
  const pageLook = productPageSettings(activeTheme?.theme?.settings || {});
  const pageClass = [
    pageLook.pageWidth === "wide" ? "container-fluid product-page-wide" : "container",
    "py-4 product-page",
    pageLook.mobileAlign === "center" ? "is-mobile-center" : "",
    pageLook.mobileBuyFull ? "is-mobile-buy-full" : "",
    `is-image-${pageLook.imageSize}`,
    `is-thumbs-${pageLook.thumbs}`,
    `is-frame-${pageLook.imageFrame}`,
    `is-info-${pageLook.infoAlign}`,
    `is-info-${pageLook.infoStyle}`,
    `is-zoom-${pageLook.imageZoom}`,
  ].filter(Boolean).join(" ");

  // Magnify follows the mouse: the photo zooms towards the point under it.
  // Set as CSS variables on the photo box, so moving the mouse never re-renders.
  const trackZoom = (event) => {
    if (pageLook.imageZoom !== "magnify") return;
    const box = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * 100;
    const y = ((event.clientY - box.top) / box.height) * 100;
    event.currentTarget.style.setProperty("--zoom-x", `${Math.min(100, Math.max(0, x))}%`);
    event.currentTarget.style.setProperty("--zoom-y", `${Math.min(100, Math.max(0, y))}%`);
  };
  const galleryCols = PRODUCT_GALLERY_COLUMNS[pageLook.galleryWidth] || 6;
  const words = pageWords(PRODUCT_TEXTS, activeTheme?.theme?.settings || {});
  const category_slug = props.match.params.categorySlug;
  const product_slug = props.match.params.productSlug;

  // Data, prices, cart, wishlist, and reviews come from the shared hook;
  // this file only lays the page out.
  const shop = useProduct(category_slug, product_slug);
  const {
    loading,
    product,
    quantity,
    setQuantity,
    selectedOptions,
    setSelectedOptions,
    inWishlist,
    reviews,
    reviewStats,
    currentPrice,
    sellingPrice,
    hasDiscount,
    discountLabel,
    stockQty,
    inStock,
  } = shop;
  const recentlyViewedList = shop.recentlyViewed;

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [brokenImages, setBrokenImages] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewInput, setReviewInput] = useState({ name: "", rating: 5, comment: "" });
  const [reviewErrors, setReviewErrors] = useState({});

  const handleAddReview = (e) => {
    e.preventDefault();
    const found = {};
    if (!reviewInput.name.trim()) found.name = "Enter your name.";
    if (!reviewInput.comment.trim()) found.comment = "Write a few words about the product.";
    setReviewErrors(found);
    if (Object.keys(found).length) return;
    setSubmittingReview(true);
    shop.submitReview({ name: reviewInput.name.trim(), rating: reviewInput.rating, comment: reviewInput.comment.trim() })
      .then((ok) => {
        if (ok) {
          setShowReviewForm(false);
          setReviewInput({ name: "", rating: 5, comment: "" });
        }
      })
      .finally(() => setSubmittingReview(false));
  };

  const handleQuantityDecrement = shop.decrementQty;
  const handleQuantityIncrement = shop.incrementQty;
  const submitAddtoCart = (e) => {
    if (e) e.preventDefault();
    shop.addToCart();
  };
  const handleBuyNow = (e) => {
    if (e) e.preventDefault();
    shop.buyNow();
  };
  const handleWishlistToggle = () => shop.toggleWishlist(galleryImages[0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category_slug, product_slug]);

  // Consolidate images (relation + primary image)
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const list = [];

    const addImg = (val) => {
      if (!val || typeof val !== "string") return;
      const clean = val.trim();
      if (clean && !list.includes(clean) && !isDefaultStoreImage(clean) && !brokenImages.includes(clean)) {
        list.push(clean);
      }
    };

    const rawGallery = product.product_image || product.productImage;
    if (rawGallery && Array.isArray(rawGallery) && rawGallery.length > 0) {
      rawGallery.forEach((item) => {
        if (typeof item === "string") {
          addImg(item);
        } else if (item && item.image) {
          addImg(item.image);
        }
      });
    } else if (product.image && !isDefaultStoreImage(product.image) && !brokenImages.includes(product.image)) {
      addImg(product.image);
    }

    // Safety filter: ensure any store logos or placeholders or broken images are completely excluded if authentic images exist
    const authenticPhotos = list.filter((img) => !isDefaultStoreImage(img) && !brokenImages.includes(img));
    if (authenticPhotos.length > 0) {
      return authenticPhotos;
    }

    // Only fallback to store logo if no real product image exists at all
    return [getDefaultStoreLogo()];
  }, [product, brokenImages]);

  const handleGalleryImageError = useCallback((imgSrc) => {
    if (!imgSrc || isDefaultStoreImage(imgSrc)) return;
    setBrokenImages((prev) => (prev.includes(imgSrc) ? prev : [...prev, imgSrc]));
  }, []);

  useEffect(() => {
    setActiveImageIndex(0);
    setBrokenImages([]);
  }, [product?.id]);

  useEffect(() => {
    if (activeImageIndex >= galleryImages.length) {
      setActiveImageIndex(0);
    }
  }, [galleryImages.length, activeImageIndex]);

  const handlePrevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (galleryImages.length <= 1) return;
    setActiveImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  }, [galleryImages.length]);

  const handleNextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (galleryImages.length <= 1) return;
    setActiveImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  }, [galleryImages.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, handlePrevImage, handleNextImage]);

  // Parts of the page the theme can reorder or hide (Theme → Product page).
  const themeSettings = activeTheme?.theme?.settings || {};
  const pageSections = productPageSections(themeSettings);
  const reviewsPart = (
    <div className="cart-table-card product-reviews p-4 p-md-5 my-5">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-3 mb-4 border-bottom">
        <div>
          <h4 className="fw-bold m-0">{words.reviewsTitle}</h4>
          <span className="text-muted" style={{ fontSize: "13.5px" }}>
            {words.reviewsSubtitle} ({reviews.length})
          </span>
        </div>
        <button
          type="button"
          onClick={() => setShowReviewForm(!showReviewForm)}
          className="btn btn-dark rounded-pill px-4 py-2 d-flex align-items-center gap-2"
          style={{ fontSize: "13.5px" }}
        >
          <FaEdit size={13} /> {showReviewForm ? "Close Form" : words.writeReview}
        </button>
      </div>

      {shop.reviewMessage && (
        <div className={`alert ${shop.reviewMessage.type === "success" ? "alert-success" : "alert-danger"} py-2 px-3 small`} role={shop.reviewMessage.type === "success" ? "status" : "alert"}>
          {shop.reviewMessage.text}
        </div>
      )}

      {/* Review Submission Form */}
      {showReviewForm && (
        <form onSubmit={handleAddReview} className="p-4 border rounded-3 bg-light mb-4" noValidate>
          <h6 className="fw-bold mb-3">Write Your Review</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: "13px" }}>Your Name</label>
              <input
                type="text"
                className={`form-control ${reviewErrors.name ? "is-invalid" : ""}`}
                placeholder="e.g. Bilal Ahmed"
                value={reviewInput.name}
                onChange={(e) => {
                  setReviewInput({ ...reviewInput, name: e.target.value });
                  setReviewErrors((prev) => ({ ...prev, name: "" }));
                }}
                required
              />
              {reviewErrors.name && <div className="invalid-feedback d-block">{reviewErrors.name}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: "13px" }}>Rating</label>
              <select
                className="form-select"
                value={reviewInput.rating}
                onChange={(e) => setReviewInput({ ...reviewInput, rating: e.target.value })}
              >
                <option value="5">★★★★★ (5 Stars - Excellent)</option>
                <option value="4">★★★★☆ (4 Stars - Very Good)</option>
                <option value="3">★★★☆☆ (3 Stars - Average)</option>
                <option value="2">★★☆☆☆ (2 Stars - Below Average)</option>
                <option value="1">★☆☆☆☆ (1 Star - Poor)</option>
              </select>
            </div>
            <div className="col-12">
              <label className="form-label" style={{ fontSize: "13px" }}>Review Comments</label>
              <textarea
                className={`form-control ${reviewErrors.comment ? "is-invalid" : ""}`}
                rows="3"
                placeholder="Write details about the product quality, shipping, etc."
                value={reviewInput.comment}
                onChange={(e) => {
                  setReviewInput({ ...reviewInput, comment: e.target.value });
                  setReviewErrors((prev) => ({ ...prev, comment: "" }));
                }}
                required
              />
              {reviewErrors.comment && <div className="invalid-feedback d-block">{reviewErrors.comment}</div>}
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary rounded-pill mt-3 px-4 fw-bold"
            style={{ background: "var(--accent)", border: "none" }}
            disabled={submittingReview}
          >
            {submittingReview ? "Submitting..." : "Submit Review"}
          </button>
        </form>
      )}

      {/* Reviews List */}
      <div className="d-flex flex-column gap-3">
        {reviews.length === 0 ? (
          <p className="text-muted text-center py-3">{words.noReviews}</p>
        ) : (
          reviews.map((rev) => (
            <div key={rev.id} className="p-3 border rounded-3 bg-white">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center gap-2">
                  <FaUserCheck color="var(--accent)" />
                  <span className="fw-bold" style={{ fontSize: "14px" }}>{rev.name}</span>
                  {rev.verified_purchase ? (
                    <span className="badge bg-success-subtle text-success" style={{ fontSize: "11px" }}>
                      <FaCheckCircle size={10} className="me-1" /> Verified Buyer
                    </span>
                  ) : null}
                </div>
                <span className="text-muted" style={{ fontSize: "12px" }}>
                  {rev.created_at ? new Date(rev.created_at).toLocaleDateString() : "Recent"}
                </span>
              </div>
              <div className="d-flex text-warning mb-2" style={{ fontSize: "13px" }}>
                {[...Array(5)].map((_, i) => (
                  i < rev.rating ? <FaStar key={i} /> : <FaRegStar key={i} />
                ))}
              </div>
              <p className="m-0 text-secondary" style={{ fontSize: "13.5px" }}>{rev.comment}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
  const recentlyViewedPart = recentlyViewedList.length > 0 && (
    <div className="product-recent my-5">
      <h4 className="fw-bold mb-4">{words.recentTitle}</h4>
      <div className="row g-4">
        {recentlyViewedList.slice(0, 4).map((item) => (
          <div className="col-6 col-md-3" key={item.id}>
            <div className="product-card-modern">
              <Link
                to={
                  item.categorySlug && item.categorySlug !== "undefined"
                    ? `/collection/${item.categorySlug}/${item.slug}`
                    : `/collection`
                }
                className="product-card-img-wrap"
              >
                <img
                  src={getProductImageUrl(item.image)}
                  alt={item.name}
                  onError={handleProductImageError}
                />
              </Link>
              <div className="product-card-body">
                {item.categoryName && (
                  <span className="product-card-category">{item.categoryName}</span>
                )}
                <Link
                  to={
                    item.categorySlug && item.categorySlug !== "undefined"
                      ? `/collection/${item.categorySlug}/${item.slug}`
                      : `/collection`
                  }
                >
                  <h4 className="product-card-title">{item.name}</h4>
                </Link>
                <div className="product-card-price-row">
                  <span className="product-current-price">
                    Rs. {item.price ? Number(item.price).toLocaleString() : 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {/* Shopify-Style Dynamic Auto On-Page SEO & Structured Data */}
      {product && (
        <SEOHead
          title={product.meta_title || product.name}
          description={product.meta_descrip || product.discription}
          keywords={product.meta_keyword}
          url={`/collection/${category_slug}/${product_slug}`}
          type="product"
          product={product}
          category={product.category}
        />
      )}

      {/* Header Banner & Breadcrumbs */}
      {pageLook.showBreadcrumbs && (
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li>
              <Link to="/collection">Collections</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li>
              <Link to={`/collection/${category_slug}`}>
                {product?.category?.name || category_slug}
              </Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">{product?.name || "Product"}</li>
          </ul>
        </div>
      </div>
      )}

      <PageColors scope={PRODUCT_SCOPE} colors={PRODUCT_COLORS} settings={activeTheme?.theme?.settings || {}} />
      <div className={pageClass} data-theme-group="product-page" data-theme-label="Product page">
        {loading || !product ? (
          <div className="row g-5">
            <div className="col-12 col-lg-6">
              <div className="skeleton-box" style={{ height: "420px", borderRadius: "16px" }}></div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="skeleton-box mb-3" style={{ height: "20px", width: "30%" }}></div>
              <div className="skeleton-box mb-3" style={{ height: "36px", width: "80%" }}></div>
              <div className="skeleton-box mb-4" style={{ height: "24px", width: "40%" }}></div>
              <div className="skeleton-box mb-4" style={{ height: "80px", width: "100%" }}></div>
              <div className="skeleton-box" style={{ height: "48px", width: "60%" }}></div>
            </div>
          </div>
        ) : (
          <>
            {/* Product Details Section */}
            <div className="row g-5 mb-5">
              {/* Left Column: Gallery & Showcase */}
              <div className={`col-12 col-lg-${galleryCols}`}>
                {galleryImages.length > 1 ? (
                  /* MULTIPLE IMAGES: Interactive Gallery */
                  <div className="product-gallery">
                    <div className="product-showcase-card">
                      {/* Top Action Controls */}
                      <div className="gallery-top-controls">
                        <button
                          type="button"
                          onClick={() => setLightboxOpen(true)}
                          className="gallery-action-btn"
                          title="View Fullscreen"
                        >
                          <FaExpand size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={handleWishlistToggle}
                          className={`gallery-action-btn wishlist ${inWishlist ? "text-danger" : ""}`}
                          title={inWishlist ? "In Wishlist" : "Add to Wishlist"}
                        >
                          <FaHeart size={16} />
                        </button>
                      </div>

                      {/* Image Counter Badge */}
                      <span className="gallery-counter-badge">
                        <FaCamera size={11} /> {activeImageIndex + 1} / {galleryImages.length}
                      </span>

                      {/* Prev / Next Chevrons */}
                      <button
                        type="button"
                        className="gallery-nav-arrow prev"
                        onClick={handlePrevImage}
                        title="Previous photo"
                        aria-label="Previous photo"
                      >
                        <FaChevronLeft size={14} />
                      </button>
                      <button
                        type="button"
                        className="gallery-nav-arrow next"
                        onClick={handleNextImage}
                        title="Next photo"
                        aria-label="Next photo"
                      >
                        <FaChevronRight size={14} />
                      </button>

                      {/* Main Image Canvas */}
                      <div
                        className="product-showcase-canvas"
                        onMouseMove={trackZoom}
                        onClick={() => setLightboxOpen(true)}
                        title="Click to view full image"
                      >
                        <img
                          key={activeImageIndex}
                          src={getProductImageUrl(galleryImages[activeImageIndex])}
                          alt={`${product.name} — view ${activeImageIndex + 1}`}
                          className="product-showcase-img"
                          onError={() => handleGalleryImageError(galleryImages[activeImageIndex])}
                        />
                      </div>
                    </div>

                    {/* Thumbnails Row */}
                    <div className="multi-gallery-thumbs">
                      {galleryImages.map((imgSrc, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`multi-thumb-item ${idx === activeImageIndex ? "active" : ""}`}
                          onClick={() => setActiveImageIndex(idx)}
                          title={`View photo ${idx + 1}`}
                        >
                          <img
                            src={getProductImageUrl(imgSrc)}
                            alt={`Thumbnail ${idx + 1}`}
                            onError={() => handleGalleryImageError(imgSrc)}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : galleryImages.length === 1 ? (
                  /* SINGLE IMAGE: Professional Luxury Showcase */
                  <div>
                    <div className="product-showcase-card">
                      {/* Certified Authentic Badge */}
                      <span className="single-verified-badge">
                        <FaShieldAlt size={12} className="text-primary" /> Verified Authentic Product
                      </span>

                      {/* Top Action Controls */}
                      <div className="gallery-top-controls">
                        <button
                          type="button"
                          onClick={() => setLightboxOpen(true)}
                          className="gallery-action-btn"
                          title="View Fullscreen"
                        >
                          <FaExpand size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={handleWishlistToggle}
                          className={`gallery-action-btn wishlist ${inWishlist ? "text-danger" : ""}`}
                          title={inWishlist ? "In Wishlist" : "Add to Wishlist"}
                        >
                          <FaHeart size={16} />
                        </button>
                      </div>

                      {/* Hero Image Canvas */}
                      <div
                        className="product-showcase-canvas"
                        onMouseMove={trackZoom}
                        onClick={() => setLightboxOpen(true)}
                        title="Click to enlarge"
                      >
                        <img
                          src={getProductImageUrl(galleryImages[0])}
                          alt={product.name}
                          className="product-showcase-img"
                          onError={() => handleGalleryImageError(galleryImages[0])}
                        />
                      </div>
                    </div>

                    {/* Professional Trust Guarantee Bar */}
                    {pageLook.showTrust && <div className="single-image-trust-bar">
                      <div className="single-trust-item">
                        <FaCheckCircle className="text-success" size={14} />
                        <span>100% Quality Inspected</span>
                      </div>
                      <div className="single-trust-item">
                        <FaTruck className="text-primary" size={14} />
                        <span>Doorstep Express Dispatch</span>
                      </div>
                      <div className="single-trust-item">
                        <FaUndo className="text-warning" size={14} />
                        <span>7-Day Return Guarantee</span>
                      </div>
                    </div>}
                  </div>
                ) : (
                  /* NO IMAGE FALLBACK */
                  <div className="product-showcase-card">
                    <div className="product-showcase-canvas">
                      <img
                        src={getDefaultStoreLogo()}
                        alt={product.name}
                        className="product-showcase-img"
                        style={{ opacity: 0.85, objectFit: "contain", maxHeight: "250px" }}
                        onError={handleProductImageError}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Information & Actions */}
              <div className={`col-12 col-lg-${12 - galleryCols} product-detail-copy`}>
                <div className="product-info-inner">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  {pageLook.showCategory && (
                  <span className="product-category-tag mb-0">
                    {product?.category?.name || "General Collection"}
                  </span>
                  )}
                  {pageLook.showBadges && (Number(product?.featured) === 1 || product?.featured === true || product?.featured === "1") && (
                    <span className="badge bg-primary text-white fw-semibold px-2.5 py-1 rounded-pill d-inline-flex align-items-center gap-1" style={{ fontSize: "11px" }}>
                      <FaStar size={10} className="text-warning" /> {words.featured}
                    </span>
                  )}
                  {pageLook.showBadges && (Number(product?.popular) === 1 || product?.popular === true || product?.popular === "1") && (
                    <span className="badge bg-danger text-white fw-semibold px-2.5 py-1 rounded-pill d-inline-flex align-items-center gap-1" style={{ fontSize: "11px" }}>
                      <FaFire size={10} /> {words.trending}
                    </span>
                  )}
                </div>

                <h1 className={`product-view-title is-size-${pageLook.titleSize}`}>{product.name}</h1>

                {/* Rating Bar */}
                {pageLook.showRating && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="d-flex text-warning" style={{ fontSize: "14px" }}>
                    <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                  </div>
                  <span className="text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>
                    {reviewStats.average} ({reviewStats.total} {words.reviewsWord})
                  </span>
                </div>
                )}

                {/* Pricing Box (Backend Calculated) */}
                <div className="price-box-modern">
                  <div className="d-flex align-items-baseline gap-3">
                    <span className="current-price-hero" style={pageLook.priceColor ? { color: pageLook.priceColor } : undefined}>
                      Rs. {Number(currentPrice).toLocaleString()}
                    </span>
                    {hasDiscount && (
                      <span className="original-price-hero">
                        Rs. {Number(sellingPrice).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {hasDiscount && (
                    <span className="discount-tag-hero">
                      {discountLabel || "Special Discount"}
                    </span>
                  )}
                </div>

                {/* Stock Status Pill */}
                {pageLook.showStock && (
                <div className="mb-4">
                  {inStock ? (
                    <span className="stock-pill in-stock">
                      <span className="stock-dot"></span> {words.inStock} (
                      {stockQty} {words.unitsReady})
                    </span>
                  ) : (
                    <span className="stock-pill out-of-stock">
                      <span className="stock-dot"></span> {words.outOfStock}
                    </span>
                  )}
                </div>
                )}

                {(product?.variant_catalog?.options || []).length > 0 && (
                  <div className="mb-4">
                    {(product.variant_catalog.options || []).map((option) => (
                      <div className="mb-3" key={option.name}>
                        <div className="fw-semibold mb-2" style={{ fontSize: "13px" }}>
                          {option.name}: <span className="fw-normal">{selectedOptions[option.name]}</span>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {(option.values || []).map((value) => {
                            const selected = String(selectedOptions[option.name]) === String(value);
                            const available = (product.variant_catalog.variants || []).some((variant) => {
                              if (String(variant.options?.[option.name]) !== String(value)) return false;
                              if (Number(variant.qty) <= 0) return false;
                              return (product.variant_catalog.options || []).every((other) => {
                                if (other.name === option.name) return true;
                                const chosen = selectedOptions[other.name];
                                if (!chosen) return true;
                                return String(variant.options?.[other.name]) === String(chosen);
                              });
                            });
                            return (
                              <button
                                type="button"
                                key={value}
                                className="btn btn-sm rounded-pill px-3"
                                style={{
                                  border: "1px solid #111",
                                  background: selected ? "#111" : "#fff",
                                  color: selected ? "#fff" : "#111",
                                  opacity: available ? 1 : 0.4,
                                }}
                                onClick={() => {
                                  setSelectedOptions((current) => ({ ...current, [option.name]: value }));
                                  setQuantity(1);
                                }}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quantity & CTA */}
                {inStock ? (
                  <div className="product-actions">
                    <div className="product-actions-main">
                    <div className="qty-stepper">
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={handleQuantityDecrement}
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <div className="qty-value">{quantity}</div>
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={handleQuantityIncrement}
                        disabled={quantity >= (stockQty || 10)}
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={submitAddtoCart}
                      className="btn-add-to-cart flex-grow-1"
                    >
                      <FaShoppingBag size={16} /> {pageLook.addLabel}
                    </button>
                    </div>

                    {pageLook.showBuyNow && (
                    <button
                      type="button"
                      onClick={handleBuyNow}
                      className="btn-buy-now"
                    >
                      {pageLook.buyLabel}
                    </button>
                    )}
                    {shop.buyError && (
                      <div className="product-buy-error" role="alert">{shop.buyError}</div>
                    )}
                  </div>
                ) : (
                  <button className="btn btn-secondary w-100 py-3 rounded-pill mb-4 fw-bold" disabled>
                    {words.unavailable}
                  </button>
                )}

                {/* Trust Points */}
                {pageLook.showTrust && (
                <div className="product-trust-strip p-3 bg-light rounded-3 border mb-4">
                  <div className="row g-2">
                    <div className="col-12 col-sm-4 d-flex align-items-center gap-2">
                      <FaTruck color="var(--accent)" />
                      <span style={{ fontSize: "12.5px", fontWeight: 600 }}>
                        {getStoreFreeShippingThreshold() === 0
                          ? words.freeShipping
                          : `${words.freeOver} Rs. ${Number(getStoreFreeShippingThreshold()).toLocaleString()}`}
                      </span>
                    </div>
                    <div className="col-12 col-sm-4 d-flex align-items-center gap-2">
                      <FaShieldAlt color="var(--accent)" />
                      <span style={{ fontSize: "12.5px", fontWeight: 600 }}>{pageLook.trustAuthentic}</span>
                    </div>
                    <div className="col-12 col-sm-4 d-flex align-items-center gap-2">
                      <FaUndo color="var(--accent)" />
                      <span style={{ fontSize: "12.5px", fontWeight: 600 }}>{pageLook.trustReturns}</span>
                    </div>
                  </div>
                </div>
                )}

                {/* Description Tabs */}
                <div className="product-spec-tabs">
                  <h6 className="fw-bold mb-2">{pageLook.descriptionTitle}</h6>
                  <p style={{ color: "var(--text-secondary)", lineHeight: 1.8, whiteSpace: "pre-line" }}>
                    {product.description ||
                      product.discription ||
                      "Premium quality verified product. Engineered to deliver exceptional durability and satisfaction."}
                  </p>
                </div>
                </div>
              </div>
            </div>

            {/* Sections below the product, in the order the theme lists them */}
            {pageSections.map((section) => {
              if (section.type === "frequently_bought") {
                return (
                  <FrequentlyBought
                    key={section.id}
                    product={product}
                    activeVariant={shop.activeVariant}
                    hasVariantChoices={shop.hasVariantChoices}
                    words={words}
                  />
                );
              }
              if (section.type === "product_reviews") return <React.Fragment key={section.id}>{reviewsPart}</React.Fragment>;
              if (section.type === "recently_viewed") return <React.Fragment key={section.id}>{recentlyViewedPart}</React.Fragment>;
              const definition = SECTION_DEFINITIONS.find((item) => item.type === section.type);
              return (
                <SectionRenderer
                  key={section.id}
                  sections={[{ id: section.id, type: section.type, enabled: true, settings: { ...(definition?.defaultSettings || {}), ...section } }]}
                  themeKey={activeTheme?.theme?.theme_key}
                  globalSettings={themeSettings}
                />
              );
            })}
          </>
        )}
      </div>

      {/* Fullscreen Lightbox Modal */}
      {lightboxOpen && galleryImages.length > 0 && (
        <div
          className="product-lightbox-backdrop"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            className="lightbox-close-btn"
            onClick={() => setLightboxOpen(false)}
            title="Close (Esc)"
          >
            <FaTimes size={18} />
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox-nav-btn prev"
                onClick={handlePrevImage}
                title="Previous (Left Arrow)"
              >
                <FaChevronLeft size={18} />
              </button>
              <button
                type="button"
                className="lightbox-nav-btn next"
                onClick={handleNextImage}
                title="Next (Right Arrow)"
              >
                <FaChevronRight size={18} />
              </button>
            </>
          )}

          <div
            className="lightbox-image-container"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getProductImageUrl(galleryImages[activeImageIndex])}
              alt={product.name}
              className="lightbox-main-img"
              onError={() => handleGalleryImageError(galleryImages[activeImageIndex])}
            />
          </div>

          {galleryImages.length > 1 && (
            <div
              className="lightbox-thumbs-strip"
              onClick={(e) => e.stopPropagation()}
            >
              {galleryImages.map((imgSrc, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`lightbox-thumb-btn ${idx === activeImageIndex ? "active" : ""}`}
                  onClick={() => setActiveImageIndex(idx)}
                >
                  <img
                    src={getProductImageUrl(imgSrc)}
                    alt=""
                    onError={() => handleGalleryImageError(imgSrc)}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
